import { call } from '@advanza/api'
import { InputField } from '@advanza/input'
import { Button, LoadingDots } from '@advanza/ui'
import Card from 'components/Card'
import Page from 'components/ui/Page'
import { useEffect, useRef, useState } from 'react'

const META_APP_ID = '966374516366920'
const META_CONFIG_ID = '987455407220468'
const WABA_PHONE_NUMBER_ID = '1120302547825446'
const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js'
const FB_SDK_ELEMENT_ID = 'facebook-jssdk'
const ALLOWED_FB_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com']

const DEFAULT_RECIPIENT = '31613775581'
const DEFAULT_TEMPLATE = 'hello_world'
const DEFAULT_LANGUAGE = 'en_US'

interface WabaIds {
    waba_id: string
    phone_number_id: string
}

interface FbLoginResponse {
    authResponse?: { code?: string }
}

interface SendResult {
    message_id: string | null
    wa_id: string | null
}

declare global {
    interface Window {
        FB?: {
            init: (options: Record<string, unknown>) => void
            login: (
                callback: (response: FbLoginResponse) => void,
                options: Record<string, unknown>,
            ) => void
        }
        fbAsyncInit?: () => void
    }
}

type ConnectStatus = 'idle' | 'connecting' | 'exchanging' | 'done' | 'error'
type SendStatus = 'idle' | 'sending' | 'done' | 'error'

const ConnectWhatsAppCard = () => {
    const [sdkReady, setSdkReady] = useState(false)
    const [status, setStatus] = useState<ConnectStatus>('idle')
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const [result, setResult] = useState<WabaIds | null>(null)

    // The auth code (from FB.login) and the WABA IDs (from postMessage) arrive
    // independently. Collect both and fire the backend call once both are present.
    const collectedRef = useRef<{ code: string | null; wabaIds: WabaIds | null }>({
        code: null,
        wabaIds: null,
    })

    const trySubmit = () => {
        const { code, wabaIds } = collectedRef.current
        if (code === null || wabaIds === null) {
            return
        }

        setStatus('exchanging')
        call('meta-waba/exchange-code', {
            json: {
                code,
                waba_id: wabaIds.waba_id,
                phone_number_id: wabaIds.phone_number_id,
            },
        }).then(
            () => {
                setStatus('done')
                setResult(wabaIds)
            },
            (err) => {
                setStatus('error')
                setErrorMsg(typeof err === 'string' ? err : JSON.stringify(err))
            },
        )
    }

    useEffect(() => {
        if (document.getElementById(FB_SDK_ELEMENT_ID)) {
            if (window.FB) {
                setSdkReady(true)
            }
            return
        }

        window.fbAsyncInit = () => {
            window.FB?.init({
                appId: META_APP_ID,
                autoLogAppEvents: true,
                xfbml: true,
                version: 'v25.0',
            })
            setSdkReady(true)
        }

        const script = document.createElement('script')
        script.id = FB_SDK_ELEMENT_ID
        script.src = FB_SDK_URL
        script.async = true
        document.body.appendChild(script)
    }, [])

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (!ALLOWED_FB_ORIGINS.includes(event.origin)) {
                return
            }

            try {
                const data = JSON.parse(event.data)
                if (data.type !== 'WA_EMBEDDED_SIGNUP') {
                    return
                }

                if (data.event === 'FINISH') {
                    collectedRef.current.wabaIds = {
                        waba_id: data.data.waba_id,
                        phone_number_id: data.data.phone_number_id,
                    }
                    trySubmit()
                }
                if (data.event === 'CANCEL') {
                    collectedRef.current = { code: null, wabaIds: null }
                    setStatus('idle')
                    setErrorMsg('User cancelled the popup')
                }
            } catch {
                // Non-JSON messages from facebook.com are routine; ignore.
            }
        }

        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [])

    const handleConnect = () => {
        if (!window.FB) {
            setStatus('error')
            setErrorMsg('Facebook SDK has not loaded yet')
            return
        }

        setStatus('connecting')
        setErrorMsg(null)
        setResult(null)
        collectedRef.current = { code: null, wabaIds: null }

        window.FB.login(
            (response) => {
                const code = response.authResponse?.code
                if (typeof code !== 'string' || code === '') {
                    setStatus('error')
                    setErrorMsg('Did not receive an auth code from Meta')
                    return
                }
                collectedRef.current.code = code
                trySubmit()
            },
            {
                config_id: META_CONFIG_ID,
                response_type: 'code',
                override_default_response_type: true,
                extras: { version: 'v4' },
            },
        )
    }

    const isBusy = status === 'connecting' || status === 'exchanging'

    return (
        <Card header="Connect a WhatsApp Business Account">
            <p className="text-sm text-gray-600 mb-4">
                Launches Meta&apos;s Embedded Signup popup so a provider can authorise Trustoo to
                send WhatsApp messages on behalf of their WhatsApp Business Account.
            </p>
            <Button appearance="primary" onClick={handleConnect} disabled={!sdkReady || isBusy}>
                {status === 'connecting' && 'Waiting for Meta popup…'}
                {status === 'exchanging' && 'Exchanging code…'}
                {!isBusy && 'Connect WhatsApp'}
            </Button>

            {!sdkReady && <p className="text-xs text-gray-500 mt-2">Loading Facebook SDK…</p>}

            {status === 'done' && result && (
                <div className="mt-4 text-sm">
                    <p className="text-green-600 font-medium">Connected successfully.</p>
                    <pre className="text-xs bg-gray-100 p-2 rounded mt-2">
                        {JSON.stringify(result, null, 2)}
                    </pre>
                </div>
            )}

            {status === 'error' && errorMsg && (
                <p className="text-red-600 text-sm mt-4">Error: {errorMsg}</p>
            )}
        </Card>
    )
}

const SendMessageCard = () => {
    const [to, setTo] = useState(DEFAULT_RECIPIENT)
    const [templateName, setTemplateName] = useState(DEFAULT_TEMPLATE)
    const [languageCode, setLanguageCode] = useState(DEFAULT_LANGUAGE)
    const [status, setStatus] = useState<SendStatus>('idle')
    const [errorMsg, setErrorMsg] = useState<string | null>(null)
    const [result, setResult] = useState<SendResult | null>(null)

    const handleSend = () => {
        if (to.trim() === '') {
            setStatus('error')
            setErrorMsg('Recipient phone number is required')
            return
        }

        setStatus('sending')
        setErrorMsg(null)
        setResult(null)

        call('meta-waba/send-test-message', {
            json: {
                to: to.trim(),
                template_name: templateName.trim() || DEFAULT_TEMPLATE,
                language_code: languageCode.trim() || DEFAULT_LANGUAGE,
            },
        }).then(
            (response: { message_id?: string | null; wa_id?: string | null }) => {
                setStatus('done')
                setResult({
                    message_id: response.message_id ?? null,
                    wa_id: response.wa_id ?? null,
                })
            },
            (err) => {
                setStatus('error')
                const message =
                    err && typeof err === 'object' && 'error' in err
                        ? String((err as { error: unknown }).error)
                        : typeof err === 'string'
                          ? err
                          : JSON.stringify(err)
                setErrorMsg(message)
            },
        )
    }

    return (
        <Card header="Send a WhatsApp message">
            <p className="text-sm text-gray-600 mb-4">
                Sends a WhatsApp template message via the Cloud API. Messages originate from the
                Trustoo test number{' '}
                <code className="bg-gray-100 px-1 rounded">{WABA_PHONE_NUMBER_ID}</code>.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <InputField
                    legend
                    placeholder="Recipient (international format, no +)"
                    inputPlaceholder="e.g. 31613775581"
                    value={to}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTo(e.target.value)}
                    disabled={status === 'sending'}
                />
                <InputField
                    legend
                    placeholder="Template name"
                    inputPlaceholder="hello_world"
                    value={templateName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTemplateName(e.target.value)
                    }
                    disabled={status === 'sending'}
                />
                <InputField
                    legend
                    placeholder="Language code"
                    inputPlaceholder="en_US"
                    value={languageCode}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setLanguageCode(e.target.value)
                    }
                    disabled={status === 'sending'}
                />
            </div>

            <div className="mt-4">
                <Button
                    appearance="primary"
                    onClick={handleSend}
                    disabled={status === 'sending' || to.trim() === ''}>
                    {status === 'sending' ? <LoadingDots /> : 'Send WhatsApp message'}
                </Button>
            </div>

            {status === 'done' && result && (
                <div className="mt-4 text-sm">
                    <p className="text-green-600 font-medium">
                        Message sent to +{result.wa_id ?? to}
                    </p>
                    {result.message_id && (
                        <p className="text-xs text-gray-500 mt-1">
                            WhatsApp message ID:{' '}
                            <code className="bg-gray-100 px-1 rounded">{result.message_id}</code>
                        </p>
                    )}
                </div>
            )}

            {status === 'error' && errorMsg && (
                <p className="text-red-600 text-sm mt-4">Error: {errorMsg}</p>
            )}
        </Card>
    )
}

const WabaTestThrowAway = () => {
    return (
        <Page>
            <div className="m-auto max-w-screen-md my-16 space-y-6">
                <div>
                    <h1>WhatsApp Business integration</h1>
                    <p className="text-sm text-gray-600 mt-2">
                        Trustoo Tech Provider tooling for onboarding providers onto the WhatsApp
                        Business Platform and sending messages on their behalf via the Cloud API.
                    </p>
                </div>

                <ConnectWhatsAppCard />
                <SendMessageCard />
            </div>
        </Page>
    )
}

export default WabaTestThrowAway
